import * as React from 'react';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import toast from 'react-hot-toast';
import { Checkbox, Stack, Typography } from '@mui/material';
import DialogConfirmation from './DialogConfirmation';
import _ from 'lodash';

export default function FormDialog({ open, setOpen, metadata, updateHeaders }) {
  const [option, setOption] = React.useState({ key: '', value: '' });
  const [confirmationOpen, setConfirmationOpen] = React.useState(false);

  const handleDelete = async ({ isConfirmed }) => {
    if (!isConfirmed) {
      setConfirmationOpen(true);
    } else {
      try {
        const { index, type } = metadata;
        const value = metadata?.options?.splice(metadata?.index_options, 1);
        await updateHeaders(index, value, type);
        toast.success('Data deleted');
        handleClose();
      } catch (error) {
        handleClose();
        toast.error(error.message);
      }
    }
  };

  const handleClose = () => {
    setOpen(false);
    setConfirmationOpen(false);
    setOption({ key: '', value: '' });
  };

  const handleSave = async () => {
    try {
      if (!option.value || !option.key) return toast.error('Please fill required field');

      const isDuplicated = metadata?.options?.filter((item) => item.key === option.key);

      // UNSAFE
      if (metadata.action_type === 'edit') {
        if (isDuplicated?.length > 1) return toast.error(`Key "${option.key}" is duplicated, please use another key.`);
      } else {
        if (isDuplicated?.length > 0) return toast.error(`Key "${option.key}" is duplicated, please use another key.`);
      }

      const type = 'options';
      const { index } = metadata;
      let value = '';
      if (metadata.isExist) {
        const _value = metadata.options;
        _value[metadata.index_options].key = option.key;
        _value[metadata.index_options].value = option.value;
        _value[metadata.index_options].label_id = option.label_id;
        _value[metadata.index_options].category = option.category;
        _value[metadata.index_options].acceptedInput = option.acceptedInput;

        value = _value;
      } else {
        const _value = metadata.options
          ? [
              ...metadata.options,
              { key: option.key, value: option.value, label_id: option.label_id, category: option.category, acceptedInput: option.acceptedInput },
            ]
          : [{ key: option.key, value: option.value, label_id: option.label_id, category: option.category, acceptedInput: option.acceptedInput }];
        value = _value;
      }
      await updateHeaders(index, value, type);
      toast.success('Option submitted');
      handleClose();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const updateQuestionCategory = (item, category) => {
    const tempValue = item.category || [];
    if (!_.isEmpty(tempValue)) {
      if (tempValue.includes(category)) {
        const _index = _.findIndex(tempValue, (val) => {
          return val.tag === category;
        });
        tempValue.splice(_index, 1);
      } else {
        tempValue.push(category);
      }
    } else {
      tempValue.push(category);
    }

    setOption({ ...option, category: tempValue });
  };

  const updateField = () => {
    setOption({
      key: metadata?.item_options?.key,
      value: metadata?.item_options?.value,
      category: metadata?.item_options?.category,
      acceptedInput: metadata?.item_options?.acceptedInput,
    });
  };

  React.useEffect(() => {
    if (metadata.options && metadata.isExist) updateField();
    // eslint-disable-next-line
  }, [metadata]);

  return (
    <div>
      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>Add Option</DialogTitle>
        <DialogContent>
          <DialogContentText>Option(s) are key:value pair, example: birth_date:Birth Day</DialogContentText>
          <TextField
            onChange={(e) => setOption({ ...option, key: e.currentTarget.value.replace(/\s+/g, '').toLowerCase() })}
            required
            margin='dense'
            id='key'
            label='Key'
            type='text'
            fullWidth
            variant='standard'
            value={option?.key?.replace(/\s+/g, '').toLowerCase()}
          />
          <TextField
            onChange={(e) => setOption({ ...option, value: e.currentTarget.value })}
            required
            autoFocus
            margin='dense'
            id='label'
            label='Label'
            type='text'
            fullWidth
            variant='standard'
            value={option.value}
          />
          <TextField
            onChange={(e) => setOption({ ...option, label_id: e.currentTarget.value })}
            required
            autoFocus
            margin='dense'
            id='label_id'
            label='Label Indonesia'
            type='text'
            fullWidth
            variant='standard'
            value={option.label_id}
          />
          {metadata.type === 'attachment' && (
            <>
              <Stack direction='column' pt={2}>
                <Typography variant='body2'>Category:</Typography>
                <Stack direction='row' alignItems='center' justifyContent='flex-end'>
                  <Stack direction='row' alignItems='center'>
                    <Checkbox checked={option?.category?.includes('gp') ? true : false} onChange={(e) => updateQuestionCategory(option, 'gp')} />
                    <Typography variant='body2'>Best Practices</Typography>
                  </Stack>
                  <Stack direction='row' alignItems='center'>
                    <Checkbox checked={option?.category?.includes('doc') ? true : false} onChange={(e) => updateQuestionCategory(option, 'doc')} />
                    <Typography variant='body2'>Document Repository</Typography>
                  </Stack>
                  <Stack direction='row' alignItems='center'>
                    <Checkbox checked={option?.category?.includes('his') ? true : false} onChange={(e) => updateQuestionCategory(option, 'his')} />
                    <Typography variant='body2'>Human Interest Story</Typography>
                  </Stack>
                </Stack>
              </Stack>
              <TextField
                onChange={(e) => setOption({ ...option, acceptedInput: e.currentTarget.value })}
                required
                autoFocus
                margin='dense'
                id='acceptedInput'
                label='Accepted Input'
                type='text'
                fullWidth
                variant='standard'
                value={option.acceptedInput}
              />
            </>
          )}
        </DialogContent>
        {/* {JSON.stringify(option)} */}
        <Stack direction='row' justifyContent='space-between' m={2}>
          <Stack direction='row'>
            {metadata.isExist && (
              <Button color='error' onClick={() => handleDelete({ isConfirmed: false })}>
                Delete?
              </Button>
            )}
          </Stack>
          <Stack direction='row'>
            <Button onClick={handleClose}>Cancel</Button>
            <Button onClick={handleSave}>Save</Button>
          </Stack>
        </Stack>
      </Dialog>
      <DialogConfirmation
        open={confirmationOpen}
        handleClose={() => setConfirmationOpen(false)}
        title='Deletion Confirmation'
        content='Are you sure want to delete this data?'
        closeButtonText='No'
        agreeButtonText='Yes'
        handleAgree={() => handleDelete({ isConfirmed: true })}
      />
    </div>
  );
}
